import { useState, useEffect, useCallback } from "react";
import { message } from "antd";
import { extractMsg } from "../utils/errorUtils";

export default function usePageState({
  fetchFn,
  filterFields = [],
  defaultPageSize = 20,
}) {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(defaultPageSize);
  const [messageApi, contextHolder] = message.useMessage();

  const applyFilter = useCallback(
    (term, targetData) => {
      const lower = term.trim().toLowerCase();
      const filtered = targetData.filter((item) => {
        if (!lower) return true;
        return filterFields.some((field) => {
          const val = typeof field === "function" ? field(item) : item[field];
          return val && val.toString().toLowerCase().includes(lower);
        });
      });
      setFilteredData(filtered);
      setPage(1);
    },
    [filterFields],
  );

  const handleSearch = useCallback(
    (val) => {
      setSearchText(val);
      applyFilter(val, data);
    },
    [applyFilter, data],
  );

  const clearFilter = useCallback(() => {
    setSearchText("");
    applyFilter("", data);
  }, [applyFilter, data]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchFn();
      const items = Array.isArray(result) ? result : [];
      setData(items);
      applyFilter(searchText, items);
    } catch (error) {
      messageApi.error({
        content: extractMsg(error, "Failed to load data"),
        key: "load-error",
      });
    } finally {
      setLoading(false);
    }
  }, [fetchFn, searchText, applyFilter, messageApi]);

  useEffect(() => {
    loadData();
  }, []);

  const onPageChange = useCallback((p, size) => {
    setPage(p);
    setPageSize(size);
  }, []);

  return {
    data,
    filteredData,
    loading,
    searchText,
    page,
    pageSize,
    messageApi,
    contextHolder,
    handleSearch,
    clearFilter,
    loadData,
    setPage,
    setPageSize,
    onPageChange,
  };
}
